import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { entitlementsApi, Entitlement } from '../../services/entitlementsApi';
import { auth } from '../../utils/firebase';
import { readActiveRole } from '../common/navGating';
import { createUserScopedCache } from './userScopedCache';

/**
 * The lawyer's plan tier, always on screen (sidebar user chip).
 *
 * Rationale: someone paying 19,990/year should be able to see WHAT they are
 * paying for without hunting for it, and someone on a trial should be
 * reminded before it lapses. Deliberately minimal — just the tier word.
 * The full plan, slot count and expiry date live on /profile, which this
 * links to.
 *
 * Renders NOTHING for free/unknown users rather than a "Free" chip: an
 * upsell nag on every screen of a legal workspace is noise, and the upgrade
 * paths already surface at the moment of need (matter cap, HC search).
 */

const TIER_LABEL: Record<string, string> = { pro: 'Pro', max: 'Max' };

/** Statuses that still let the lawyer work — MUST equal the backend's
 *  ACCESS_GRANTING_STATUSES (pinned by backend/tests/test_plan_badge_parity.py).
 *  The badge used a deny-list (hide only expired/revoked), so it could say
 *  MAX while every AI call returned "subscription required". */
export const ACCESS_GRANTING_STATUSES = ['trialing', 'active', 'past_due', 'suspended', 'paused'];

export type PlanBadgeView =
  | { kind: 'trial'; label: 'Trial' }
  | { kind: 'paid'; label: string }
  | { kind: 'lapsed'; label: 'Expired'; tier: string }
  | null;

/**
 * What the badge says, for the workspace the lawyer is IN.
 *
 * It read the highest tier across ALL practices, so a lawyer with Max in civil
 * and an expired criminal plan saw MAX while working in criminal, where every
 * AI feature returned 402 (dev, 2026-10-20). That hides the one fact that
 * should make them renew. With an active workspace the badge now reflects that
 * practice only, and a lapsed plan says so. With none (before the workspace
 * is chosen) it falls back to the best LIVE plan held anywhere.
 */
export function planBadgeFor(ents: Entitlement[], activeRole: string | null): PlanBadgeView {
  const live = (e: Entitlement) => ACCESS_GRANTING_STATUSES.includes(e.status);
  const view = (e: Entitlement): PlanBadgeView => {
    if (!TIER_LABEL[e.tier]) return null;
    if (!live(e)) return { kind: 'lapsed', label: 'Expired', tier: TIER_LABEL[e.tier] };
    return e.status === 'trialing' ? { kind: 'trial', label: 'Trial' } : { kind: 'paid', label: TIER_LABEL[e.tier] };
  };

  if (activeRole) {
    const here = ents.find((e) => e.vertical === activeRole);
    return here ? view(here) : null;
  }
  const paid = ents.filter((e) => TIER_LABEL[e.tier] && live(e));
  if (paid.length === 0) return null;
  return view(paid.find((e) => e.tier === 'max') ?? paid[0]);
}

/** Cached per signed-in USER, not per page. The sidebar mounts on every
 *  route and entitlements change only on purchase/expiry, so refetching per
 *  navigation would be waste — but sign-out does not reload the page, and a
 *  plain module cache showed the PREVIOUS user's plan to the next one
 *  (2026-10-17). See userScopedCache. */
const cache = createUserScopedCache<Entitlement[]>();
let inflight: { uid: string; promise: Promise<Entitlement[]> } | null = null;

const PlanBadge: React.FC = () => {
  const uid = auth.currentUser?.uid ?? null;
  const [ents, setEnts] = useState<Entitlement[] | null>(() => cache.get(uid));

  useEffect(() => {
    setEnts(cache.get(uid));
    if (!uid || cache.get(uid)) return;
    let cancelled = false;
    // Share one request across concurrent mounts — of the SAME user only.
    if (!inflight || inflight.uid !== uid) {
      const promise = entitlementsApi.getMine();
      inflight = { uid, promise };
      promise
        .then((data) => cache.set(uid, data))
        .catch(() => { /* badge stays hidden */ })
        .finally(() => { if (inflight?.promise === promise) inflight = null; });
    }
    inflight.promise
      .then((data) => {
        // Silent on failure: a badge is an affordance, never a blocker.
        // Showing nothing is correct — showing a GUESSED tier would not be.
        if (!cancelled && auth.currentUser?.uid === uid) setEnts(data);
      })
      .catch(() => { /* badge stays hidden */ });
    return () => { cancelled = true; };
  }, [uid]);

  if (!ents) return null;

  const badge = planBadgeFor(ents, readActiveRole());
  if (!badge) return null;

  return (
    <Link
      to="/profile"
      title={
        badge.kind === 'trial'
          ? 'You are on a free trial — see your plan'
          : badge.kind === 'lapsed'
            ? `Your ${badge.tier} plan for this practice has expired — renew to keep using AI features`
            : `You are on the ${badge.label} plan — see details`
      }
      className={[
        'shrink-0 px-1.5 py-px rounded-sm font-plex text-[9px] font-bold uppercase tracking-[0.08em]',
        'transition-opacity hover:opacity-80',
        badge.kind === 'trial'
          ? 'bg-brass/20 text-brass'
          : badge.kind === 'lapsed'
            ? 'bg-crimson-500/20 text-crimson-200 ring-1 ring-crimson-400/40'
            : 'bg-brass text-chamber-ink',
      ].join(' ')}
    >
      {badge.label}
    </Link>
  );
};

export default PlanBadge;
